import {useCallback, useEffect, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import {onSnapshot, doc} from "firebase/firestore";
import firebase from "../../core/firebase";
import {moduleBillingService} from "../module-billing.service";
import {StripePaymentDialog} from "./stripe-payment-dialog";
import application from "../../core/application";

type CreditPackage = {
  payAmount: number;
  receiveCredits: number;
};

const AI_CREDIT_PACKAGES: CreditPackage[] = [
  {payAmount: 20, receiveCredits: 40},
  {payAmount: 50, receiveCredits: 167},
  {payAmount: 100, receiveCredits: 400},
  {payAmount: 300, receiveCredits: 3000},
];

export const AiCreditsSection = ({
  hideHeader = false,
  disablePurchase = false,
}: {
  hideHeader?: boolean;
  disablePurchase?: boolean;
}) => {
  const theme = useTheme();
  const [credits, setCredits] = useState<number | null>(null);
  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    clientSecret: string;
    amountBrl: number;
    credits: number;
  } | null>(null);
  const [loadingPackage, setLoadingPackage] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Real-time listener for AI credit balance
  useEffect(() => {
    const user = application.user.get();
    if (!user) return;

    const uid = (user as {id: string}).id;
    const subRef = doc(firebase.db, `customerSubscriptions/${uid}`);
    const unsubscribe = onSnapshot(subRef, (snap) => {
      if (snap.exists()) {
        setCredits((snap.data()?.aiCredits as number) ?? 0);
      }
    });
    return unsubscribe;
  }, []);

  const handleBuyCredits = async (pkg: CreditPackage) => {
    if (disablePurchase) return;
    setLoadingPackage(pkg.payAmount);
    setError(null);
    try {
      const result = await moduleBillingService.stripePurchaseAiCredits(
        pkg.payAmount as 20 | 50 | 100 | 300
      );
      setPaymentDialog({
        open: true,
        clientSecret: result.clientSecret,
        amountBrl: pkg.payAmount,
        credits: result.credits,
      });
    } catch (err) {
      setError((err as Error).message || "Erro ao iniciar compra.");
    } finally {
      setLoadingPackage(null);
    }
  };

  const handlePaymentSuccess = useCallback(async () => {
    const creditsToAdd = paymentDialog?.credits ?? 0;
    setPaymentDialog(null);
    setSuccessMsg(
      `Pagamento confirmado! ${creditsToAdd.toLocaleString("pt-BR")} créditos devem aparecer no saldo em instantes (atualização automática).`
    );
    setTimeout(() => setSuccessMsg(null), 8000);
  }, [paymentDialog?.credits]);

  return (
    <Box>
      <Stack direction={{xs: "column", sm: "row"}} justifyContent="space-between" alignItems={{xs: "flex-start", sm: "center"}} spacing={2} mb={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            {!hideHeader ? (
              <>
                <Typography variant="h6" fontWeight={800}>
                  Créditos de IA
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use em automações e recursos inteligentes.
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Selecione um pacote para adicionar saldo e continuar sua operação.
              </Typography>
            )}
          </Box>
        </Stack>
        <Chip
          label={
            credits === null ?
              "Carregando saldo..." :
              `${credits.toLocaleString("pt-BR")} créditos disponíveis`
          }
          color="primary"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            fontWeight: 800,
          }}
        />
      </Stack>

      {successMsg && (
        <Alert severity="success" sx={{mb: 2, borderRadius: 2}}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{mb: 2}}>{error}</Alert>
      )}
      {disablePurchase ? (
        <Alert severity="warning" sx={{mb: 2, borderRadius: 2}}>
          Existe uma cobrança de créditos de IA pendente. Finalize ou cancele essa cobrança para comprar novos créditos.
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        {AI_CREDIT_PACKAGES.map((pkg) => (
          <Grid item xs={12} sm={4} key={pkg.payAmount}>
            <Card
              elevation={0}
              sx={{
                bgcolor: alpha(theme.palette.background.paper, 1),
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.15),
                borderRadius: 3,
                transition: "border-color 0.2s",
                "&:hover": {borderColor: alpha(theme.palette.primary.main, 0.4)},
              }}
            >
              <CardContent sx={{p: 2, "&:last-child": {pb: 2}}}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Typography color="primary.main" sx={{ fontWeight: 900, fontSize: "1.35rem", lineHeight: 1}}>
                    {pkg.receiveCredits.toLocaleString("pt-BR")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">créditos</Typography>
                  <Typography fontWeight={800} sx={{ fontSize: "0.95rem" }}>
                    R$ {pkg.payAmount.toFixed(0)},00
                  </Typography>
                  {pkg.payAmount === 300 && (
                    <Box sx={{px: 1, py: 0.35, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), border: "1px solid", borderColor: alpha(theme.palette.primary.main, 0.2)}}>
                      <Typography color="primary.main" sx={{ fontSize: "0.7rem", fontWeight: 800}}>MELHOR CUSTO-BENEFÍCIO</Typography>
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    startIcon={loadingPackage === pkg.payAmount ? undefined : <AddIcon />}
                    disabled={loadingPackage !== null || disablePurchase}
                    onClick={() => handleBuyCredits(pkg)}
                    sx={{
                      fontWeight: 700,
                      mt: 1,
                    }}
                  >
                    {loadingPackage === pkg.payAmount
                      ? <CircularProgress size={16} sx={{color: "#fff"}} />
                      : "Comprar"
                    }
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {paymentDialog && (
        <StripePaymentDialog
          open={paymentDialog.open}
          clientSecret={paymentDialog.clientSecret}
          amountBrl={paymentDialog.amountBrl}
          title="Comprar Créditos de IA"
          description={`Você receberá ${paymentDialog.credits.toLocaleString("pt-BR")} créditos de IA após a confirmação do pagamento.`}
          onSuccess={handlePaymentSuccess}
          onClose={() => setPaymentDialog(null)}
        />
      )}
    </Box>
  );
};
